import { normalizeTroops } from "./logistics-math.js";
import type { ReinforcementState } from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { exactRecordKeys, recordObject } from "./record-validation.js";
import { validateWaveTimers } from "./wave-timing.js";

export function initialReinforcement(): ReinforcementState {
  return {
    shares: { "guard-robot": {}, "raider-robot": {}, "siege-gun": {} },
    modes: {},
    gathered: {},
    cursors: { "guard-robot": 0, "raider-robot": 0, "siege-gun": 0 },
  };
}

export function cloneReinforcement(state: ReinforcementState): ReinforcementState {
  return Object.freeze({
    ...(state.timers
      ? {
          timers: Object.freeze(
            Object.fromEntries(
              Object.entries(state.timers).map(([id, timer]) => [id, Object.freeze({ ...timer })]),
            ),
          ),
        }
      : {}),
    shares: Object.freeze(
      Object.fromEntries(
        UNIT_IDS.map((unit) => [unit, Object.freeze({ ...state.shares[unit] })]),
      ) as ReinforcementState["shares"],
    ),
    modes: Object.freeze({ ...state.modes }),
    gathered: Object.freeze(
      Object.fromEntries(
        Object.entries(state.gathered).map(([id, troops]) => [id, Object.freeze({ ...troops })]),
      ),
    ),
    cursors: Object.freeze({ ...state.cursors }),
  });
}

export function validateShares(value: unknown, borders: readonly string[]): Record<string, number> {
  const source = recordObject(value, "production shares");
  let sum = 0;
  for (const [id, percent] of Object.entries(source)) {
    if (
      !borders.includes(id) ||
      !Number.isInteger(percent) ||
      (percent as number) < 0 ||
      (percent as number) > 100
    )
      throw new TypeError("Shares must be whole percentages for active borders");
    sum += percent as number;
  }
  if (sum > 100) throw new TypeError("Production shares cannot exceed 100%");
  return { ...source } as Record<string, number>;
}

export function validateReinforcement(
  value: unknown,
  borders: readonly string[],
): ReinforcementState {
  const source = recordObject(value, "reinforcement");
  exactRecordKeys(source, [
    "shares",
    "modes",
    "gathered",
    "cursors",
    ...(source.timers === undefined ? [] : ["timers"]),
  ]);
  const shares = recordObject(source.shares, "shares");
  const cursors = recordObject(source.cursors, "routing cursors");
  exactRecordKeys(shares, UNIT_IDS);
  exactRecordKeys(cursors, UNIT_IDS);
  for (const cursor of Object.values(cursors))
    if (!Number.isInteger(cursor) || (cursor as number) < 0 || (cursor as number) > 99)
      throw new TypeError("Invalid routing cursor");
  const modes = recordObject(source.modes, "dispatch modes");
  const gathered = recordObject(source.gathered, "gathered troops");
  for (const [id, mode] of Object.entries(modes))
    if (!borders.includes(id) || (mode !== "continuous" && mode !== "gather"))
      throw new TypeError("Invalid dispatch mode");
  for (const id of Object.keys(gathered))
    if (!borders.includes(id)) throw new TypeError("Invalid gathered destination");
  return {
    ...(source.timers === undefined ? {} : { timers: validateWaveTimers(source.timers, borders) }),
    shares: Object.fromEntries(
      UNIT_IDS.map((unit) => [unit, validateShares(shares[unit], borders)]),
    ) as ReinforcementState["shares"],
    modes: { ...modes } as ReinforcementState["modes"],
    gathered: Object.fromEntries(
      Object.entries(gathered).map(([id, troops]) => [
        id,
        normalizeTroops(recordObject(troops, "gathered troops")),
      ]),
    ),
    cursors: { ...cursors } as ReinforcementState["cursors"],
  };
}
